/**
 * `/demo/terrain` — renders terrain COG via Giro3D ColorLayer without gTwin.
 *
 * Proves the terrain pipeline end-to-end: converter COG → TiTiler colormap →
 * Giro3D ColorLayer (terrain-color-source adapter path) with hillshade,
 * slope/aspect color maps, contours, and elevation query — all via the
 * TerrainToolCard.
 *
 * Prerequisites:
 *   - TiTiler running (default http://localhost:8000)
 *   - A terrain COG reachable by TiTiler (converter output or sample DEM)
 *
 * Override the default sample with `?url=<cog-url>` query parameter or
 * `VITE_DEMO_TERRAIN_COG_URL` env var.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ViewerHost, type EngineId } from '@geosoft/viewer-sdk';
import { DEFAULT_ENGINE_ROUTING_POLICY } from '@/sdk-stub/viewer-sdk/policy/engineRouting';
import { Giro3DAdapter } from '@/engines/giro3d/Giro3DAdapter';
import { CanvasMapAdapter } from '@/engines/openlayers/CanvasMapAdapter';
import { sceneItemToLayer } from '@/app/sceneItemToLayer';
import { Banner, tokens } from '@/design';
import { TerrainToolCard } from '@/components/terrain/TerrainToolCard';
import { useViewerStore } from '@/state/useViewerStore';
import { selectByLayerId } from '@/app/sceneSelection';

const DEFAULT_TERRAIN_COG =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEMO_TERRAIN_COG_URL) ||
  '';

const SAMPLE_COGS: ReadonlyArray<{ label: string; url: string; bbox: [number, number, number, number] }> = [
  {
    label: 'Tokyo Minato-ku DEM (PLATEAU converted)',
    url: '/data/out/minato_dem.cog.tif',
    bbox: [139.75, 35.666, 139.775, 35.675],
  },
  {
    label: 'Copernicus 30m DSM (Tokyo tile)',
    url: 'https://copernicus-dem-30m.s3.amazonaws.com/Copernicus_DSM_COG_10_N35_00_E139_00_DEM/Copernicus_DSM_COG_10_N35_00_E139_00_DEM.tif',
    bbox: [139.0, 35.0, 140.0, 36.0],
  },
];

const TILES_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
  'http://localhost:8000'
).replace(/\/+$/, '');

interface CogTileJson {
  bounds?: [number, number, number, number];
  center?: [number, number, number?];
  minzoom?: number;
  maxzoom?: number;
}

function buildTilejsonUrl(cogUrl: string): string {
  const url = new URL(`${TILES_BASE}/cog/WebMercatorQuad/tilejson.json`);
  url.searchParams.set('url', cogUrl);
  url.searchParams.append('bidx', '1');
  return url.toString();
}

export function TerrainDemoRoute() {
  const [searchParams] = useSearchParams();
  const urlFromQuery = searchParams.get('url')?.trim();

  const containerRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<ViewerHost | null>(null);
  const adapterRef = useRef<InstanceType<typeof Giro3DAdapter> | null>(null);
  const mountedRef = useRef(false);

  const [cogUrl, setCogUrl] = useState(() => urlFromQuery || DEFAULT_TERRAIN_COG || SAMPLE_COGS[0].url);
  const [tileJson, setTileJson] = useState<CogTileJson | null>(null);
  const [status, setStatus] = useState<string>('Initialising…');
  const [hostGen, setHostGen] = useState(0);
  const [layerMounted, setLayerMounted] = useState(false);

  useEffect(() => {
    const u = searchParams.get('url')?.trim();
    if (u) setCogUrl(u);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const trimmed = cogUrl.trim();
    if (!trimmed) { setStatus('Enter a COG URL to load terrain.'); return; }

    setStatus('Fetching COG metadata from TiTiler…');
    void (async () => {
      try {
        const res = await fetch(buildTilejsonUrl(trimmed));
        if (!res.ok) throw new Error(`TiTiler tilejson HTTP ${res.status}`);
        const json = (await res.json()) as CogTileJson;
        if (!cancelled) {
          setTileJson(json);
          setStatus('COG bounds loaded — adding terrain layer…');
        }
      } catch (e) {
        if (!cancelled) {
          setTileJson(null);
          setStatus(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cogUrl]);

  const terrainLayer = useMemo(() => {
    const bbox = tileJson?.bounds ?? [0, 0, 1, 1];
    const layer = sceneItemToLayer(
      {
        id: 'terrain-demo-layer',
        layerKind: 'terrain',
        crs: 'EPSG:4326',
        bbox,
        endpoints: { terrain: cogUrl.trim() },
        style: { family: 'terrain' },
      },
      'terrain',
    );
    layer.label = 'Terrain DEM (COG via TiTiler)';
    return layer;
  }, [cogUrl, tileJson]);

  useEffect(() => {
    let cancelled = false;
    if (mountedRef.current || !containerRef.current) return;
    mountedRef.current = true;

    void (async () => {
      try {
        let host: ViewerHost;
        let usingCanvas = false;
        try {
          const adapter = new Giro3DAdapter();
          host = new ViewerHost({
            container: containerRef.current!,
            primaryEngine: DEFAULT_ENGINE_ROUTING_POLICY.defaults.terrain as EngineId,
            adapters: [adapter],
          });
          await host.mount();
          adapterRef.current = adapter;
        } catch (webglErr) {
          console.warn('[TerrainDemoRoute] Giro3D WebGL failed — Canvas 2D fallback', webglErr);
          usingCanvas = true;
          adapterRef.current = null;
          host = new ViewerHost({
            container: containerRef.current!,
            primaryEngine: 'openlayers',
            adapters: [new CanvasMapAdapter()],
          });
          await host.mount();
        }
        if (cancelled) {
          await host.unmount();
          return;
        }
        hostRef.current = host;
        setHostGen((n) => n + 1);
        setStatus(
          usingCanvas
            ? 'Canvas 2D mode (no GPU) — loading DEM tiles…'
            : 'Giro3D engine ready — loading terrain…',
        );
      } catch (e) {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      void hostRef.current?.unmount();
      hostRef.current = null;
      adapterRef.current = null;
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || hostGen === 0 || !tileJson) return;

    void (async () => {
      try {
        setStatus('Adding terrain ColorLayer (TiTiler colormap path)…');
        for (const existing of host.listLayers()) {
          await host.removeLayer(existing.id);
        }
        await host.addLayer(terrainLayer);

        useViewerStore.setState({
          sceneSelection: selectByLayerId(terrainLayer.id, { layerKind: 'terrain' }),
        });

        const center = tileJson.center;
        const bounds = tileJson.bounds;
        if (center) {
          host.flyTo({ lonLat: [center[0], center[1]], zoom: Math.max(center[2] ?? 11, 12) });
        } else if (bounds) {
          host.flyTo({
            lonLat: [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2],
            zoom: 12,
          });
        }

        setLayerMounted(true);
        setStatus('Terrain layer loaded. Use the tool card on the right to toggle hillshade, slope, contours, etc.');
      } catch (e) {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [terrainLayer, hostGen, tileJson]);

  const terrainToolCard = useViewerStore((s) => s.terrainToolCard);
  useEffect(() => {
    if (!adapterRef.current || !layerMounted) return;
    try {
      adapterRef.current.applyTerrainToolCardState(terrainToolCard);
    } catch (err) {
      console.warn('[TerrainDemo] terrain state sync:', err);
    }
  }, [terrainToolCard, layerMounted]);

  const handlePreset = useCallback((url: string) => {
    setCogUrl(url);
    setTileJson(null);
    setLayerMounted(false);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
      data-route="/demo/terrain"
    >
      <Banner
        severity="info"
        title="Terrain demo — bypasses gTwin"
        body={
          <span>
            Renders a DEM COG via Giro3D ColorLayer (TiTiler colormap path). Does not load or write
            to any <code>.gtwin</code> project.{' '}
            <Link to="/projects" style={{ color: tokens.color.accent.primary }}>
              Projects
            </Link>
          </span>
        }
        narrationKey="terrain-demo-bypass-banner"
      />

      <header
        style={{
          flexShrink: 0,
          padding: tokens.space[3],
          borderBottom: tokens.border.thin,
          background: tokens.color.surface[1],
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[3], alignItems: 'center' }}>
          <strong style={{ fontSize: tokens.font.size.md }}>Terrain DEM demo (Giro3D ColorLayer)</strong>
          <Link to="/projects" style={{ color: tokens.color.accent.primary, fontSize: tokens.font.size.sm }}>
            &larr; Projects
          </Link>
          <Link to="/demo/raster-cog" style={{ color: tokens.color.accent.primary, fontSize: tokens.font.size.sm }}>
            Raster COG demo
          </Link>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[2], marginTop: tokens.space[2] }}>
          {SAMPLE_COGS.map((s) => (
            <button
              key={s.url}
              onClick={() => handlePreset(s.url)}
              style={{
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                borderRadius: tokens.radius.input,
                border: cogUrl === s.url ? `${tokens.border.width.thin * 2}px solid ${tokens.color.accent.primary}` : tokens.border.thin,
                background: cogUrl === s.url ? tokens.color.surface[2] : tokens.color.surface[0],
                color: tokens.color.ink.primary,
                cursor: 'pointer',
                fontSize: tokens.font.size.sm,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <label
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
            marginTop: tokens.space[2],
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: tokens.font.size.sm }}>COG URL (reachable by TiTiler)</span>
          <input
            type="url"
            value={cogUrl}
            onChange={(e) => setCogUrl(e.target.value)}
            placeholder="http://localhost:3011/converter-output/DTM.cog.tif"
            style={{
              flex: `1 1 ${tokens.layout.popoverSm}px`,
              minHeight: tokens.density.touchMin,
              maxWidth: `min(${tokens.layout.standaloneStateExtraWide}px, 100%)`,
              padding: tokens.space[2],
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
            }}
          />
        </label>

        <p
          style={{
            margin: `${tokens.space[1]}px 0 0`,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.muted,
          }}
        >
          {status}
        </p>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
        <div ref={containerRef} style={{ flex: 1, minHeight: 0 }} />

        {layerMounted && (
          <aside
            style={{
              width: 320,
              flexShrink: 0,
              overflowY: 'auto',
              borderLeft: tokens.border.thin,
              background: tokens.color.surface[1],
              padding: tokens.space[3],
            }}
          >
            <TerrainToolCard forceVisible layer={terrainLayer} />
          </aside>
        )}
      </div>
    </div>
  );
}
